// NativeBookingPicker — the engine-owned replacement for the HubSpot meetings
// iframe, used by both BookingPage (/book/:person/:key) and LetsTalkPage
// (/lets-talk/:program).
//
// MIGRATION CONTRACT — why onFallback exists: this component probes the native
// booking API first. A 404 means "no active native booking type for this
// host+key" (deliberately indistinguishable from nonexistent, per
// booking-routes.ts), and the parent page falls back to its existing HubSpot
// embed. So each of Ron's links flips to native exactly when its booking_types
// row is activated — no flag day, no per-link code changes. After firing
// onFallback this component renders nothing; the parent owns the fallback.
//
// The flow extends the platform's two-step email-first pattern (RegisterCard in
// WebinarPage.tsx) with two branches no other capture surface has:
//   - a SLOT step before email (pick → email → names → confirmed), and
//   - a lost-race branch: step 1 can 409 when someone else takes the slot in
//     the seconds between render and submit. That's recoverable, so it gets a
//     fresh slot list and a gentle notice — never a dead error card.
//
// Deliberately NO localStorage done-latch (unlike webinar registration):
// booking again is a legitimate, common action.
//
// Styling reads the LetsTalkPage CSS custom properties with slate fallbacks, so
// the picker is brand-tinted inside a program landing and clean-slate on the
// bare booking page, from one file.

import { useEffect, useMemo, useState } from 'react';
import type { CSSProperties, FormEvent } from 'react';
import { fetchWithRetry } from '../lib/fetchRetry';
import { getAttributionPayload } from '../attribution';
import { SmsOptIn } from './SmsOptIn';

const API = import.meta.env.VITE_API_BASE_URL as string;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

interface BookingTypePayload {
  bookingKey: string;
  title: string;
  description: string | null;
  durationMinutes: number;
  hostTimezone: string;
  /** True on types owed to a customer (BKOS kickoff): offer "None of these
   *  times work for me", which tells the team to arrange it by hand. */
  offersNoFit?: boolean;
  /** Another host's page to offer first when none of these times fit (Lisa's
   *  kickoff page behind Mitchell's). Null unless that page is switched on. */
  noFitFallback?: { hostName: string; hostTimezone: string; url: string } | null;
  /** True when a booking here ticks the buyer's Roadmap step (the kickoff). */
  ticksRoadmap?: boolean;
  /** The host who covers this page when its host is full or away (Annalou
   *  behind Mitchell). Null unless her page is switched on. */
  cover?: { hostName: string; hostTimezone: string; url: string; callNoun: string } | null;
  /** The promise the page makes once the buyer half is switched on
   *  ("Mitchell will reach out within one business day…"). Null = today's words. */
  noFitPromise?: string | null;
  host: { slug: string; name: string; role: string | null } | null;
  /** Region routing + intake (worker booking-region.ts, mct_85e5ce3f-bb6):
   *  the page asks for the email, and CC's questions when there are any,
   *  before it shows times, then shows the calendar for the member's region. */
  routesByRegion?: boolean;
  intake?: IntakeSetPayload | null;
}

interface IntakeQuestionPayload {
  id: string;
  wording: string;
  help_text: string | null;
  type: string;
  options: Array<{ value: string; label: string }>;
  required: boolean;
}

interface IntakeSetPayload {
  slug: string;
  title: string;
  intro: string | null;
  questions: IntakeQuestionPayload[];
}

/** Where the intake sent this member: their region's calendar. */
interface RouteState {
  personSlug: string;
  bookingKey: string;
  intakeId: string | null;
  active: boolean;
}

export interface BookingBrandPayload {
  name: string;
  logoUrl: string | null;
  logoLinkUrl: string | null;
  palette: {
    textDark?: string; primary?: string; cta?: string; ctaText?: string;
    tint?: string; canvas?: string; deep?: string;
  } | null;
  fonts: { heading?: { family?: string }; body?: { family?: string } } | null;
  designTokens: { radius?: { button?: number; card?: number } } | null;
  googleFontsUrl: string | null;
}

interface SlotsResponse {
  bookingType: BookingTypePayload;
  brand?: BookingBrandPayload;
  slots: string[]; // UTC ISO instants, sorted
  calendarUnavailable: boolean;
  /** A region-routed page served without an intake id: no times until the
   *  member has answered the intake. */
  needsIntake?: boolean;
}

export interface NativeBookingPickerProps {
  personSlug: string;
  bookingKey: string;
  prefill?: { email?: string; firstName?: string; lastName?: string };
  /** Fired once when the native API 404s — no active native type. The parent
   *  must stop rendering the picker and show its HubSpot embed instead. */
  onFallback: () => void;
  /** Rebook mode (the manage page): the engine already knows who this is, so
   *  the email/names steps are skipped — pick a slot, confirm, done. The POST
   *  goes to the tokened manage endpoint instead of the public book route. */
  rebookToken?: string;
  /** Fired once with the booking type's resolved brand so the PARENT page can
   *  dress its chrome (logo, palette vars, fonts) to match the funnel. The
   *  picker itself needs nothing — its styles already read the --prog-* vars
   *  the parent sets. */
  onBrand?: (brand: BookingBrandPayload | null) => void;
}

// The booker's own zone — every time in the UI renders in it. Falling back to
// undefined lets Intl use the environment default, which is the same thing.
function bookerTimezone(): string | undefined {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || undefined;
  } catch {
    return undefined;
  }
}

// 'YYYY-MM-DD' key for grouping slots into the booker's local days. en-CA
// gives ISO ordering for free, which keeps day keys sortable as strings.
function localDayKey(iso: string, tz: string | undefined): string {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: tz, year: 'numeric', month: '2-digit', day: '2-digit',
  }).format(new Date(iso));
}

function dayLabel(iso: string, tz: string | undefined): { weekday: string; date: string } {
  const d = new Date(iso);
  return {
    weekday: new Intl.DateTimeFormat('en-US', { timeZone: tz, weekday: 'short' }).format(d),
    date: new Intl.DateTimeFormat('en-US', { timeZone: tz, month: 'short', day: 'numeric' }).format(d),
  };
}

function timeLabel(iso: string, tz: string | undefined): string {
  return new Intl.DateTimeFormat('en-US', {
    timeZone: tz, hour: 'numeric', minute: '2-digit',
  }).format(new Date(iso)).toLowerCase();
}

function fullWhenLabel(iso: string, tz: string | undefined): string {
  return new Intl.DateTimeFormat('en-US', {
    timeZone: tz, weekday: 'long', month: 'long', day: 'numeric',
    hour: 'numeric', minute: '2-digit', timeZoneName: 'short',
  }).format(new Date(iso));
}

export function NativeBookingPicker({ personSlug, bookingKey, prefill, onFallback, rebookToken, onBrand }: NativeBookingPickerProps) {
  // The booker's zone, detected from the device but changeable: a VPN, travel
  // or a wrong device clock otherwise shows another country's times with no way
  // to tell (Michael, testing as an Australian, 14 Oct 2026, mct_196fc60b-e02).
  const [tz, setTz] = useState<string | undefined>(bookerTimezone);

  const [load, setLoad] = useState<'loading' | 'ready' | 'unavailable' | 'error'>('loading');
  const [retryNonce, setRetryNonce] = useState(0);
  const [data, setData] = useState<SlotsResponse | null>(null);

  const [flow, setFlow] = useState<'pick' | 'email' | 'names' | 'rebook-confirm' | 'confirmed'>('pick');
  const [selectedDay, setSelectedDay] = useState<string | null>(null);
  const [selectedSlot, setSelectedSlot] = useState<string | null>(null);
  const [email, setEmail] = useState(prefill?.email ?? '');
  const [firstName, setFirstName] = useState(prefill?.firstName ?? '');
  const [lastName, setLastName] = useState(prefill?.lastName ?? '');
  const [phone, setPhone] = useState('');
  const [bookingId, setBookingId] = useState<string | null>(null);
  const [joinUrl, setJoinUrl] = useState<string | null>(null);
  const [confirmedStart, setConfirmedStart] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Set when a 409 pulled the rug — rendered as a notice above the fresh grid,
  // not an error card, because the situation is fully recoverable.
  const [slotTakenNotice, setSlotTakenNotice] = useState(false);
  // Set once the intake has routed this member. Every later call (slots, hold,
  // "none of these times work") goes to the routed calendar, not the page's.
  const [route, setRoute] = useState<RouteState | null>(null);
  const [answers, setAnswers] = useState<Record<string, string | string[]>>({});
  const slug = route?.personSlug ?? personSlug;
  const key = route?.bookingKey ?? bookingKey;

  useEffect(() => {
    let cancelled = false;
    setLoad('loading');
    (async () => {
      try {
        // A dark regional calendar has no slots to fetch; the page shows the
        // "not open yet" card from the intake answer instead.
        if (route && !route.active) { setLoad('ready'); return; }
        const qs = route?.intakeId
          ? `?intake=${encodeURIComponent(route.intakeId)}`
          : rebookToken ? `?rebook=${encodeURIComponent(rebookToken)}` : '';
        const res = await fetchWithRetry(
          `${API}/api/public/bookings/${encodeURIComponent(slug)}/${encodeURIComponent(key)}/slots${qs}`,
        );
        if (cancelled) return;
        if (res.status === 404 && !route) { onFallback(); return; }
        if (res.status === 404) { setLoad('error'); return; }
        if (!res.ok) { setLoad('error'); return; }
        const d = await res.json() as SlotsResponse;
        if (cancelled) return;
        setData(d);
        onBrand?.(d.brand ?? null);
        setLoad(d.calendarUnavailable ? 'unavailable' : 'ready');
      } catch {
        if (!cancelled) setLoad('error');
      }
    })();
    return () => { cancelled = true; };
    // onFallback intentionally omitted: parents pass inline closures, and
    // re-probing because a parent re-rendered would double-fire the fallback.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug, key, route?.intakeId, route?.active, retryNonce]);

  const slotsByDay = useMemo(() => {
    const map = new Map<string, string[]>();
    for (const s of data?.slots ?? []) {
      const key = localDayKey(s, tz);
      const list = map.get(key) ?? [];
      list.push(s);
      map.set(key, list);
    }
    return map;
  }, [data, tz]);

  const dayKeys = useMemo(() => Array.from(slotsByDay.keys()).sort(), [slotsByDay]);
  // Any time open in the next 7 days? When not, a page with a cover host offers hers.
  const slotsWithinWeek = useMemo(() => {
    const now = Date.now();
    return (data?.slots ?? []).some((s) => { const t = Date.parse(s); return t >= now && t <= now + 7 * 86_400_000; });
  }, [data?.slots]);
  const activeDay = selectedDay && slotsByDay.has(selectedDay) ? selectedDay : dayKeys[0] ?? null;

  async function refreshSlots(): Promise<void> {
    setSelectedSlot(null);
    setFlow('pick');
    setRetryNonce((n) => n + 1);
  }

  async function submitRebook() {
    if (busy || !selectedSlot || !rebookToken) return;
    setError(null);
    setBusy(true);
    try {
      const r = await fetch(
        `${API}/api/public/bookings/manage/${encodeURIComponent(rebookToken)}/rebook`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ startIso: selectedSlot }),
        },
      );
      const d = await r.json().catch(() => ({})) as { ok?: boolean; joinUrl?: string | null; startIso?: string | null; slotTaken?: boolean; error?: string };
      if (r.status === 409 && d.slotTaken) {
        setSlotTakenNotice(true);
        setBusy(false);
        await refreshSlots();
        return;
      }
      if (!r.ok || !d.ok) throw new Error(d.error ?? 'Could not move your booking — please try again.');
      setJoinUrl(d.joinUrl ?? null);
      setConfirmedStart(d.startIso ?? selectedSlot);
      setFlow('confirmed');
      setBusy(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      setBusy(false);
    }
  }

  async function submitEmail(e: FormEvent) {
    e.preventDefault();
    await holdSlot(selectedSlot);
  }

  async function holdSlot(slot: string | null) {
    if (busy || !slot) return;
    setError(null);
    if (!EMAIL_RE.test(email.trim())) { setError('Please enter a valid email address.'); return; }
    setBusy(true);
    try {
      const r = await fetch(
        `${API}/api/public/bookings/${encodeURIComponent(slug)}/${encodeURIComponent(key)}/book`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            email: email.trim(),
            startIso: slot,
            timezone: tz ?? null,
            attribution: getAttributionPayload(),
            intakeId: route?.intakeId ?? null,
          }),
        },
      );
      const d = await r.json().catch(() => ({})) as { ok?: boolean; bookingId?: string; slotTaken?: boolean; error?: string };
      if (r.status === 409 && d.slotTaken) {
        // Someone else took it between render and submit. Fresh slots, gentle
        // notice, back to the grid — the one branch that must never dead-end.
        setSlotTakenNotice(true);
        setBusy(false);
        await refreshSlots();
        return;
      }
      if (!r.ok || !d.ok || !d.bookingId) throw new Error(d.error ?? 'Could not hold that time — please try again.');
      setBookingId(d.bookingId);
      setSelectedSlot(slot);
      setFlow('names');
      setBusy(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      setBusy(false);
    }
  }

  async function submitNames(e: FormEvent) {
    e.preventDefault();
    if (busy || !bookingId) return;
    setError(null);
    if (!firstName.trim()) { setError('Please enter your first name.'); return; }
    setBusy(true);
    try {
      const r = await fetch(
        `${API}/api/public/bookings/${encodeURIComponent(slug)}/${encodeURIComponent(key)}/book`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            bookingId,
            firstName: firstName.trim(),
            lastName: lastName.trim() || null,
            // Contact phone for host follow-up — NOT SMS consent (that's the
            // separate, disclosed opt-in on the confirmation card). Server
            // normalizes via toE164 and drops anything dirty.
            phone: phone.trim() || null,
          }),
        },
      );
      const d = await r.json().catch(() => ({})) as { ok?: boolean; joinUrl?: string | null; startIso?: string | null; error?: string };
      if (!r.ok || !d.ok) throw new Error(d.error ?? 'Could not complete your booking — please try again.');
      setJoinUrl(d.joinUrl ?? null);
      setConfirmedStart(d.startIso ?? selectedSlot);
      setFlow('confirmed');
      setBusy(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      setBusy(false);
    }
  }

  async function submitIntake(e: FormEvent) {
    e.preventDefault();
    if (busy) return;
    setError(null);
    if (!EMAIL_RE.test(email.trim())) { setError('Please enter a valid email address.'); return; }
    for (const q of data?.bookingType.intake?.questions ?? []) {
      const v = answers[q.id];
      const empty = Array.isArray(v) ? v.length === 0 : !String(v ?? '').trim();
      if (q.required && empty) { setError(`Please answer "${q.wording}"`); return; }
    }
    setBusy(true);
    try {
      const r = await fetch(
        `${API}/api/public/bookings/${encodeURIComponent(personSlug)}/${encodeURIComponent(bookingKey)}/intake`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email: email.trim(), answers }),
        },
      );
      const d = await r.json().catch(() => ({})) as {
        ok?: boolean; error?: string; intakeId?: string | null;
        routed?: { personSlug: string; bookingKey: string; active: boolean } | null;
      };
      if (!r.ok || !d.ok) throw new Error(d.error ?? 'Something went wrong — please try again.');
      // No calendar for their region at all: stay on this page, with the
      // "none of these times work" request as the way through.
      setRoute(d.routed
        ? { personSlug: d.routed.personSlug, bookingKey: d.routed.bookingKey, intakeId: d.intakeId ?? null, active: d.routed.active }
        : { personSlug, bookingKey, intakeId: null, active: false });
      setBusy(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      setBusy(false);
    }
  }

  const hostFirst = data?.bookingType.host?.name?.split(' ')[0] ?? 'us';
  const intakeFirst = !rebookToken && !route && !!data && (data.needsIntake || !!data.bookingType.routesByRegion || !!data.bookingType.intake);

  // ---- load states -----------------------------------------------------------

  if (load === 'loading') {
    return <div style={frame}><p style={muted}>Finding available times…</p></div>;
  }
  if (load === 'error') {
    return (
      <div style={frame}>
        <p style={muted}>We couldn't load available times.</p>
        <button style={secondaryBtn} onClick={() => setRetryNonce((n) => n + 1)}>Try again</button>
      </div>
    );
  }
  if (load === 'unavailable') {
    // The host's calendar could not be read. Honest copy — this is NOT "fully
    // booked", and pretending it is would read as rejection.
    return (
      <div style={frame}>
        <h3 style={cardTitle}>We can't show times right now</h3>
        <p style={muted}>
          Our calendar connection is having a moment. Please try again shortly —
          or email us and we'll set your call up personally.
        </p>
        <button style={secondaryBtn} onClick={() => setRetryNonce((n) => n + 1)}>Try again</button>
      </div>
    );
  }

  // ---- intake (before any times) ---------------------------------------------

  if (intakeFirst && data) {
    const set = data.bookingType.intake ?? null;
    return (
      <div style={frame}>
        {data.bookingType.title && <h2 style={pickTitleStyle}>{data.bookingType.title}</h2>}
        {data.bookingType.description && <p style={pickDescStyle}>{data.bookingType.description}</p>}
        <form onSubmit={submitIntake}>
          {set?.title && <h3 style={cardTitle}>{set.title}</h3>}
          {set?.intro && <p style={muted}>{set.intro}</p>}
          <label style={label} htmlFor="nbp-intake-email">Your email</label>
          <input
            id="nbp-intake-email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@yourfirm.com"
            style={input}
            autoFocus
          />
          <p style={fieldNote}>Use the email you bought with, so we show you the right times and tick off your Roadmap.</p>
          {(set?.questions ?? []).map((q) => (
            <IntakeField key={q.id} q={q} value={answers[q.id]} onChange={(v) => setAnswers((a) => ({ ...a, [q.id]: v }))} />
          ))}
          {error && <p style={errorLine}>{error}</p>}
          <button type="submit" style={primaryBtn} disabled={busy}>
            {busy ? 'One moment…' : 'See available times'}
          </button>
        </form>
      </div>
    );
  }

  if (route && !route.active && flow !== 'confirmed') {
    // Their region's calendar is not open yet (Ron's Australian hours before
    // he sets them), or there is none: never another region's times. The
    // "none of these times work" request is how they get a call meanwhile.
    return (
      <div style={frame}>
        <h3 style={cardTitle}>We're opening times for your region</h3>
        <p style={muted}>
          Tell us what suits you where you are, and we'll set your call up with you.
        </p>
        {data?.bookingType.offersNoFit && (
          <NoFitRequest
            personSlug={route.personSlug}
            bookingKey={route.bookingKey}
            hostFirst={hostFirst}
            initialEmail={email}
            initialName=""
            tz={tz}
            openByDefault
            fallback={null}
          fallbackNoun="kickoff calls"
            promise={null}
          />
        )}
      </div>
    );
  }

  // ---- confirmed -------------------------------------------------------------

  if (flow === 'confirmed' && confirmedStart) {
    return (
      <div style={frame}>
        <div style={confirmBadge}>✓</div>
        <h3 style={cardTitle}>You're booked in</h3>
        <p style={whenLine}>{fullWhenLabel(confirmedStart, tz)}</p>
        <p style={muted}>
          {joinUrl
            ? <>Your confirmation email has everything you need — your Zoom link and a calendar invite. No need to join until it's time.</>
            : <>We're setting up your meeting room — the link will arrive by email shortly, with a calendar invite attached.</>}
        </p>
        {/* Post-conversion placement only, per the carrier contract in
            SmsOptIn.tsx — the SMS ask never sits beside the primary flow.
            Skipped in rebook mode: the offer was made at the original booking,
            and this surface has no client-side email to hand the component. */}
        {!rebookToken && <SmsOptIn
          email={email.trim() || null}
          firstName={firstName.trim() || null}
          lastName={lastName.trim() || null}
          source="booking"
          sourceDetail={`${personSlug}/${bookingKey}`}
          heading="Want a text reminder before your call?"
          style={{ textAlign: 'left', marginTop: 8 }}
        />}
      </div>
    );
  }

  // ---- rebook confirm --------------------------------------------------------

  if (flow === 'rebook-confirm' && selectedSlot) {
    return (
      <div style={frame}>
        <p style={pickedLine}>
          {fullWhenLabel(selectedSlot, tz)}
          {' · '}
          <button style={linkBtn} onClick={() => { setFlow('pick'); setError(null); }} type="button">
            change time
          </button>
        </p>
        <p style={muted}>
          Move your call to this time? Your old time is released and a fresh
          confirmation lands in your inbox.
        </p>
        {error && <p style={errorLine}>{error}</p>}
        <button type="button" style={primaryBtn} disabled={busy} onClick={submitRebook}>
          {busy ? 'Moving your booking…' : 'Confirm new time'}
        </button>
      </div>
    );
  }

  // ---- email / names steps ---------------------------------------------------

  if ((flow === 'email' || flow === 'names') && selectedSlot) {
    return (
      <div style={frame}>
        <p style={pickedLine}>
          {fullWhenLabel(selectedSlot, tz)}
          {' · '}
          <button
            style={linkBtn}
            onClick={() => { setFlow('pick'); setError(null); }}
            type="button"
          >
            change time
          </button>
        </p>
        {flow === 'email' ? (
          <form onSubmit={submitEmail}>
            <label style={label} htmlFor="nbp-email">Your email</label>
            <input
              id="nbp-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@yourfirm.com"
              style={input}
              autoFocus
            />
            {data?.bookingType.offersNoFit && data.bookingType.ticksRoadmap !== false && (
              <p style={fieldNote}>Use the email you bought with, so this ticks off your first-week Roadmap.</p>
            )}
            {error && <p style={errorLine}>{error}</p>}
            <button type="submit" style={primaryBtn} disabled={busy}>
              {busy ? 'Holding your time…' : 'Continue'}
            </button>
          </form>
        ) : (
          <form onSubmit={submitNames}>
            <p style={muted}>Your time is held. One last thing — who should {hostFirst} expect?</p>
            <label style={label} htmlFor="nbp-fn">First name</label>
            <input id="nbp-fn" value={firstName} onChange={(e) => setFirstName(e.target.value)} style={input} autoFocus />
            <label style={label} htmlFor="nbp-ln">Last name</label>
            <input id="nbp-ln" value={lastName} onChange={(e) => setLastName(e.target.value)} style={input} />
            <label style={label} htmlFor="nbp-ph">Mobile number <span style={optionalTag}>optional</span></label>
            <input
              id="nbp-ph"
              type="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="+1 555 123 4567"
              style={input}
            />
            <p style={fieldNote}>In case {hostFirst} needs to reach you about your call.</p>
            {error && <p style={errorLine}>{error}</p>}
            <button type="submit" style={primaryBtn} disabled={busy}>
              {busy ? 'Booking…' : 'Confirm booking'}
            </button>
          </form>
        )}
      </div>
    );
  }

  // ---- pick ------------------------------------------------------------------

  return (
    <div style={frame}>
      {slotTakenNotice && (
        <p style={noticeLine}>That time was just taken — here are the latest available times.</p>
      )}
      {data && (
        <>
          {data.bookingType.title && <h2 style={pickTitleStyle}>{data.bookingType.title}</h2>}
          {data.bookingType.description && <p style={pickDescStyle}>{data.bookingType.description}</p>}
          <p style={headerLine}>
            {data.bookingType.durationMinutes}-minute call
            {data.bookingType.host ? ` with ${data.bookingType.host.name}` : ''}
          </p>
          <TimeZoneLine tz={tz} onChange={setTz} />
        </>
      )}
      {data?.bookingType.cover && !rebookToken && !slotsWithinWeek && (
        <div style={noFitBox}>
          <p style={cardTitle}>{hostFirst} is fully booked or away this week</p>
          <p style={muted}>
            {data.bookingType.cover.hostName.split(' ')[0]} also runs {data.bookingType.cover.callNoun}, from {cityOf(data.bookingType.cover.hostTimezone)}.
          </p>
          <a href={data.bookingType.cover.url} style={{ ...primaryBtn, display: 'block', textAlign: 'center', textDecoration: 'none' }}>
            See {data.bookingType.cover.hostName.split(' ')[0]}'s times
          </a>
        </div>
      )}
      {dayKeys.length === 0 ? (
        <p style={muted}>
          {data?.bookingType.offersNoFit && !rebookToken
            ? "No times are open in the next two weeks. Tell us what suits you below and we'll find a slot that works."
            : "No times are open in the next two weeks. Email us and we'll find a slot that works."}
        </p>
      ) : (
        <>
          <div style={dayStrip}>
            {dayKeys.map((key) => {
              const first = slotsByDay.get(key)![0];
              const { weekday, date } = dayLabel(first, tz);
              const active = key === activeDay;
              return (
                <button
                  key={key}
                  type="button"
                  onClick={() => { setSelectedDay(key); setSelectedSlot(null); }}
                  style={active ? dayBtnActive : dayBtn}
                >
                  <span style={{ display: 'block', fontSize: 11, opacity: 0.75 }}>{weekday}</span>
                  <span style={{ display: 'block', fontWeight: 600 }}>{date}</span>
                </button>
              );
            })}
          </div>
          <div style={timeGrid}>
            {(activeDay ? slotsByDay.get(activeDay) ?? [] : []).map((iso) => (
              <button
                key={iso}
                type="button"
                style={timeBtn}
                disabled={busy}
                onClick={() => {
                  setSelectedSlot(iso);
                  setSlotTakenNotice(false);
                  setError(null);
                  // The intake already has their email: hold the time now.
                  if (route?.intakeId && !rebookToken) { void holdSlot(iso); return; }
                  setFlow(rebookToken ? 'rebook-confirm' : 'email');
                }}
              >
                {timeLabel(iso, tz)}
              </button>
            ))}
          </div>
        </>
      )}
      {error && flow === 'pick' && <p style={errorLine}>{error}</p>}
      {data?.bookingType.offersNoFit && !rebookToken && (
        <NoFitRequest
          personSlug={slug}
          bookingKey={key}
          hostFirst={hostFirst}
          initialEmail={email}
          initialName={[firstName, lastName].filter(Boolean).join(' ')}
          tz={tz}
          openByDefault={dayKeys.length === 0}
          fallback={data.bookingType.noFitFallback ?? (data.bookingType.cover ? { hostName: data.bookingType.cover.hostName, hostTimezone: data.bookingType.cover.hostTimezone, url: data.bookingType.cover.url } : null)}
          fallbackNoun={data.bookingType.cover?.callNoun ?? 'kickoff calls'}
          promise={data.bookingType.noFitPromise ?? null}
        />
      )}
    </div>
  );
}

/** One CC intake question. Types are CC's closed list (short_text, long_text,
 *  number, single_choice, multi_choice, phone); the worker drops any other. */
function IntakeField(props: {
  q: IntakeQuestionPayload;
  value: string | string[] | undefined;
  onChange: (v: string | string[]) => void;
}) {
  const { q } = props;
  const id = `nbp-q-${q.id}`;
  const text = typeof props.value === 'string' ? props.value : '';
  const picked = Array.isArray(props.value) ? props.value : [];
  return (
    <div>
      <label style={label} htmlFor={id}>
        {q.wording}{!q.required && <> <span style={optionalTag}>optional</span></>}
      </label>
      {q.type === 'long_text' ? (
        <textarea id={id} value={text} onChange={(e) => props.onChange(e.target.value)} rows={3} style={{ ...input, resize: 'vertical' }} />
      ) : q.type === 'single_choice' ? (
        <select id={id} value={text} onChange={(e) => props.onChange(e.target.value)} style={input}>
          <option value="">Choose one</option>
          {q.options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      ) : q.type === 'multi_choice' ? (
        <div id={id} style={{ marginBottom: 12 }}>
          {q.options.map((o) => (
            <label key={o.value} style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 14, margin: '4px 0' }}>
              <input
                type="checkbox"
                checked={picked.includes(o.value)}
                onChange={(e) => props.onChange(e.target.checked ? [...picked, o.value] : picked.filter((p) => p !== o.value))}
              />
              {o.label}
            </label>
          ))}
        </div>
      ) : (
        <input
          id={id}
          type={q.type === 'number' ? 'number' : q.type === 'phone' ? 'tel' : 'text'}
          value={text}
          onChange={(e) => props.onChange(e.target.value)}
          style={input}
        />
      )}
      {q.help_text && <p style={fieldNote}>{q.help_text}</p>}
    </div>
  );
}

/**
 * "None of these times work for me" — the manual fallback on a booking owed to
 * a customer. Mitchell hosts every BKOS kickoff from London, so some time zones
 * will find nothing sensible; this tells the team (a task plus Slack, via
 * POST …/no-fit) so a person arranges the call. Never shown on funnel pages.
 */
function NoFitRequest(props: {
  personSlug: string;
  bookingKey: string;
  hostFirst: string;
  initialEmail: string;
  initialName: string;
  tz: string | undefined;
  openByDefault: boolean;
  fallback: { hostName: string; hostTimezone: string; url: string } | null;
  fallbackNoun: string;
  /** Set once the buyer half is switched on (mct_32793343-21e): the page asks
   *  for their time zone and two or three times, and makes this promise. */
  promise: string | null;
}) {
  const [open, setOpen] = useState(props.openByDefault);
  const [email, setEmail] = useState(props.initialEmail);
  const [name, setName] = useState(props.initialName);
  const [note, setNote] = useState('');
  const [zone, setZone] = useState(props.tz ?? '');
  const zones = useMemo(() => timeZoneOptions(props.tz), [props.tz]);
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (!EMAIL_RE.test(email.trim())) { setError('Please enter a valid email.'); return; }
    if (props.promise && !note.trim()) { setError('Please tell us two or three times that suit you.'); return; }
    setBusy(true); setError(null);
    try {
      const r = await fetch(
        `${API}/api/public/bookings/${encodeURIComponent(props.personSlug)}/${encodeURIComponent(props.bookingKey)}/no-fit`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email: email.trim(), name: name.trim() || null, note: note.trim() || null, timezone: (props.promise ? zone : props.tz) || null }),
        },
      );
      if (!r.ok) throw new Error(String(r.status));
      setSent(true);
    } catch {
      setError("That didn't go through. Please try again, or email us and we'll sort it.");
    } finally {
      setBusy(false);
    }
  }

  if (sent && props.promise) {
    return (
      <div style={noFitBox}>
        <p style={cardTitle}>Thanks, we have your times</p>
        <p style={muted}>{props.promise} We've emailed you a copy of what you sent.</p>
      </div>
    );
  }
  if (sent) {
    return (
      <div style={noFitBox}>
        <p style={cardTitle}>Thanks, we have it</p>
        <p style={muted}>We'll email you to find a time that works where you are.</p>
      </div>
    );
  }
  if (!open) {
    return (
      <p style={{ ...muted, marginTop: 22 }}>
        <button type="button" style={linkBtn} onClick={() => setOpen(true)}>
          None of these times work for me
        </button>
      </p>
    );
  }
  return (
    <form onSubmit={submit} style={noFitBox}>
      <p style={cardTitle}>None of these times work?</p>
      {props.fallback && (
        <>
          <p style={muted}>
            {props.fallback.hostName} also runs {props.fallbackNoun}, from {cityOf(props.fallback.hostTimezone)}.
          </p>
          <a href={props.fallback.url} style={{ ...primaryBtn, display: 'block', textAlign: 'center', textDecoration: 'none', marginBottom: 18 }}>
            See {props.fallback.hostName.split(' ')[0]}'s times
          </a>
        </>
      )}
      <p style={muted}>
        {props.promise
          ? <>{props.fallback ? 'Or tell' : 'Tell'} us your time zone and two or three times that suit you. {props.promise}</>
          : props.fallback
          ? "Or tell us when suits you and we'll arrange your call personally."
          : <>Tell us when suits you and we'll arrange your call with {props.hostFirst} personally.</>}
      </p>
      <label style={label} htmlFor="nbp-nf-email">Your email</label>
      <input id="nbp-nf-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@yourfirm.com" style={input} />
      <label style={label} htmlFor="nbp-nf-name">Your name <span style={optionalTag}>optional</span></label>
      <input id="nbp-nf-name" value={name} onChange={(e) => setName(e.target.value)} style={input} />
      {props.promise && (
        <>
          <label style={label} htmlFor="nbp-nf-zone">Your time zone</label>
          <select id="nbp-nf-zone" value={zone} onChange={(e) => setZone(e.target.value)} style={input}>
            {!zone && <option value="">Choose your time zone</option>}
            {zones.map((z) => <option key={z} value={z}>{z.replace(/_/g, ' ')}</option>)}
          </select>
        </>
      )}
      <label style={label} htmlFor="nbp-nf-note">
        {props.promise ? 'Two or three days and times that suit you' : 'What days and times usually work for you?'}
      </label>
      <textarea
        id="nbp-nf-note"
        value={note}
        onChange={(e) => setNote(e.target.value)}
        rows={3}
        maxLength={1000}
        required={!!props.promise}
        placeholder={props.promise
          ? 'For example: Tuesday 8am, Wednesday 7pm, or any weekday before 9am'
          : 'For example: weekday mornings before 10am'}
        style={{ ...input, resize: 'vertical', fontFamily: 'inherit' }}
      />
      {props.promise
        ? zone && <p style={fieldNote}>We'll read those times in your time zone ({zone.replace(/_/g, ' ')}).</p>
        : props.tz && <p style={fieldNote}>We'll read that in your time zone ({props.tz}).</p>}
      {error && <p style={errorLine}>{error}</p>}
      <button type="submit" style={primaryBtn} disabled={busy}>
        {busy ? 'Sending…' : 'Send'}
      </button>
    </form>
  );
}

/** "Times shown in Sydney time (AEDT)" with a way to change it. Every slot,
 *  day heading and confirmation on the page renders in this zone, and it is
 *  the zone sent with the booking. */
function TimeZoneLine({ tz, onChange }: { tz: string | undefined; onChange: (tz: string) => void }) {
  const [open, setOpen] = useState(false);
  const zones = useMemo(() => timeZoneOptions(tz), [tz]);
  if (!tz) return null;
  let abbr = '';
  try {
    abbr = new Intl.DateTimeFormat('en-US', { timeZone: tz, timeZoneName: 'short' })
      .formatToParts(new Date()).find((p) => p.type === 'timeZoneName')?.value ?? '';
  } catch { /* unknown zone: show the city alone */ }
  return (
    <p style={headerLine} data-testid="nbp-timezone">
      Times shown in <strong>{cityOf(tz)}</strong> time{abbr ? ` (${abbr})` : ''}.{' '}
      {open ? (
        <select
          aria-label="Your time zone"
          value={tz}
          onChange={(e) => { onChange(e.target.value); setOpen(false); }}
          style={{ fontSize: 13, padding: '2px 6px', maxWidth: 260 }}
        >
          {zones.map((z) => <option key={z} value={z}>{z.replace(/_/g, ' ')}</option>)}
        </select>
      ) : (
        <button
          type="button"
          onClick={() => setOpen(true)}
          style={{ background: 'none', border: 'none', padding: 0, color: 'inherit', textDecoration: 'underline', cursor: 'pointer', font: 'inherit' }}
        >
          Change time zone
        </button>
      )}
    </p>
  );
}

/** Every IANA zone the browser knows, with the buyer's detected one first, so
 *  a buyer whose device zone is wrong (travel, a VPN) can set their own. */
function timeZoneOptions(detected: string | undefined): string[] {
  let all: string[] = [];
  try {
    all = (Intl as unknown as { supportedValuesOf?: (k: string) => string[] }).supportedValuesOf?.('timeZone') ?? [];
  } catch { /* older browser: just the detected zone */ }
  if (detected && !all.includes(detected)) all = [detected, ...all];
  return all;
}

/** "America/Toronto" → "Toronto", for "Lisa also runs kickoff calls, from Toronto". */
function cityOf(timezone: string): string {
  const last = timezone.split('/').pop() ?? timezone;
  return last.replace(/_/g, ' ');
}

// ---- styles ------------------------------------------------------------------
// CSS vars with slate fallbacks: brand-tinted inside LetsTalkPage (which sets
// --prog-* on <html>), clean neutral on the bare BookingPage.

const v = (prop: string, fallback: string) => `var(${prop}, ${fallback})`;

const frame: CSSProperties = {
  padding: '28px 26px',
  minHeight: 320,
};

const headerLine: CSSProperties = {
  margin: '0 0 18px',
  fontSize: 13.5,
  fontWeight: 600,
  color: v('--prog-deep', '#334155'),
};

const pickTitleStyle: CSSProperties = {
  margin: '0 0 8px',
  fontSize: 21,
  fontWeight: 800,
  color: v('--prog-deep', '#0F172A'),
  letterSpacing: '-0.01em',
};

const pickDescStyle: CSSProperties = {
  margin: '0 0 16px',
  fontSize: 14.5,
  lineHeight: 1.6,
  color: '#475569',
};

const dayStrip: CSSProperties = {
  display: 'flex',
  gap: 8,
  overflowX: 'auto',
  paddingBottom: 6,
  marginBottom: 16,
};

const dayBtn: CSSProperties = {
  padding: '8px 14px',
  borderRadius: 10,
  border: '1px solid #E2E8F0',
  background: '#fff',
  color: '#0F172A',
  cursor: 'pointer',
  fontSize: 13,
  flexShrink: 0,
  fontFamily: 'inherit',
};

const dayBtnActive: CSSProperties = {
  ...dayBtn,
  border: `1.5px solid ${v('--prog-primary', '#0F172A')}`,
  background: v('--prog-tint', '#F1F5F9'),
};

const timeGrid: CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fill, minmax(96px, 1fr))',
  gap: 8,
};

const timeBtn: CSSProperties = {
  padding: '10px 6px',
  borderRadius: 10,
  border: `1px solid ${v('--prog-primary', '#0F172A')}`,
  background: '#fff',
  color: v('--prog-primary', '#0F172A'),
  cursor: 'pointer',
  fontSize: 13.5,
  fontWeight: 600,
  fontFamily: 'inherit',
};

const pickedLine: CSSProperties = {
  margin: '0 0 18px',
  fontSize: 14.5,
  fontWeight: 700,
  color: '#0F172A',
};

const linkBtn: CSSProperties = {
  background: 'none',
  border: 'none',
  padding: 0,
  color: v('--prog-primary', '#0F172A'),
  textDecoration: 'underline',
  cursor: 'pointer',
  fontSize: 13,
  fontFamily: 'inherit',
};

const label: CSSProperties = {
  display: 'block',
  fontSize: 12.5,
  fontWeight: 600,
  color: '#475569',
  margin: '0 0 6px',
};

const input: CSSProperties = {
  width: '100%',
  boxSizing: 'border-box',
  padding: '11px 12px',
  borderRadius: 10,
  border: '1px solid #CBD5E1',
  fontSize: 15,
  marginBottom: 14,
  fontFamily: 'inherit',
};

const primaryBtn: CSSProperties = {
  display: 'inline-block',
  padding: '12px 24px',
  borderRadius: v('--radius-button', '10px'),
  border: 'none',
  background: v('--prog-primary', '#0F172A'),
  color: '#fff',
  fontWeight: 700,
  fontSize: 15,
  cursor: 'pointer',
  fontFamily: 'inherit',
};

const optionalTag: CSSProperties = {
  fontWeight: 400,
  color: '#94A3B8',
  fontSize: 11.5,
};

const fieldNote: CSSProperties = {
  margin: '-8px 0 14px',
  fontSize: 12.5,
  color: '#94A3B8',
};

const secondaryBtn: CSSProperties = {
  padding: '9px 18px',
  borderRadius: 10,
  border: '1px solid #CBD5E1',
  background: '#fff',
  color: '#0F172A',
  fontWeight: 600,
  fontSize: 13.5,
  cursor: 'pointer',
  fontFamily: 'inherit',
};

const muted: CSSProperties = {
  margin: '0 0 16px',
  fontSize: 14,
  lineHeight: 1.6,
  color: '#64748B',
};

const errorLine: CSSProperties = {
  margin: '0 0 12px',
  fontSize: 13.5,
  color: '#B91C1C',
};

const noticeLine: CSSProperties = {
  margin: '0 0 14px',
  padding: '10px 14px',
  borderRadius: 10,
  background: '#FEF3C7',
  color: '#92400E',
  fontSize: 13.5,
};

const cardTitle: CSSProperties = {
  margin: '0 0 10px',
  fontSize: 19,
  fontWeight: 800,
  color: '#0F172A',
};

const whenLine: CSSProperties = {
  margin: '0 0 12px',
  fontSize: 15.5,
  fontWeight: 700,
  color: v('--prog-primary', '#0F172A'),
};

const confirmBadge: CSSProperties = {
  width: 44,
  height: 44,
  borderRadius: '50%',
  background: '#DCFCE7',
  color: '#15803D',
  fontSize: 22,
  fontWeight: 800,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  marginBottom: 14,
};

const noFitBox: CSSProperties = {
  marginTop: 24,
  paddingTop: 20,
  borderTop: '1px solid #e2e8f0',
};
